import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface RateLimitInfo {
  attempts_last_xtime: number;
  attempts_today: number;
  can_attempt: boolean;
}

export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  
  // Check if user is already authenticated
  useEffect(() => {
    const checkSession = () => {
      const authData = localStorage.getItem('admin_auth_data');
      if (authData) {
        try {
          const { authenticated, sessionId, loginTime } = JSON.parse(authData);
          
          // Check if session is still valid (24 hours)
          const now = Date.now();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          
          if (authenticated && sessionId && loginTime && (now - loginTime < twentyFourHours)) {
            setIsAuthenticated(true);
            return;
          }
        } catch (err) {
          console.error('Error parsing auth data:', err);
        }
      }
      
      // No valid authentication found
      setIsAuthenticated(false);
    };

    // Initial check on page load
    checkSession();

    // Listen for authentication events
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_auth_data') {
        // Authentication state changed
        checkSession();
      }
    };

    // Add storage event listener
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const checkRateLimit = async (clientIp: string): Promise<RateLimitInfo> => {
    try {
      const { data, error } = await supabase.rpc('check_admin_rate_limit', {
        client_ip: clientIp
      });
      
      if (error) throw error;
      return data as unknown as RateLimitInfo;
    } catch (err) {
      console.error('Rate limit check failed:', err);
      return { attempts_last_xtime: 0, attempts_today: 0, can_attempt: true };
    }
  };

  const logAttempt = async (success: boolean, sessionId?: string) => {
    try {
      // Get client IP (this is a simplified approach - in production you'd get this from server)
      const clientIp = '127.0.0.1'; // Placeholder - would need proper IP detection
      
      await supabase.from('admin_access_attempts').insert({
        ip_address: clientIp,
        user_agent: navigator.userAgent,
        success,
        session_id: sessionId,
        // Note: country and city would be populated by server-side IP geolocation
      });
    } catch (err) {
      console.error('Failed to log attempt:', err);
    }
  };

  const authenticate = async (password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Check rate limiting first
      const clientIp = '127.0.0.1'; // Placeholder
      const rateLimitCheck = await checkRateLimit(clientIp);
      setRateLimitInfo(rateLimitCheck);

      if (!rateLimitCheck.can_attempt) {
        const errorMsg = rateLimitCheck.attempts_today >= 15 
          ? 'Too many failed attempts today. Please try again tomorrow.'
          : 'Too many attempts. Please wait 15 minutes before trying again.';
        setError(errorMsg);
        return false;
      }

      // Get the stored password from settings
      const { data: settings, error: settingsError } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'admin_secret_code')
        .single();

      if (settingsError) {
        throw new Error('Failed to verify credentials');
      }

      const isValid = password === settings.value;

      if (isValid) {
        const sessionId = Math.random().toString(36);
        const loginTime = Date.now();
        
        // Store authentication data in localStorage
        localStorage.setItem('admin_auth_data', JSON.stringify({
          authenticated: true,
          sessionId: sessionId,
          loginTime: loginTime
        }));
        
        // Force UI update with a small delay to ensure proper state propagation
        setIsAuthenticated(true);
        
        // Force a page refresh to ensure UI updates (temporary fix)
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
        await logAttempt(true, sessionId);
        return true;
      } else {
        await logAttempt(false);
        setError('Invalid password. Please check your password and try again.');
        return false;
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Authentication failed. Please check your connection and try again.');
      await logAttempt(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Remove authentication data from localStorage
    localStorage.removeItem('admin_auth_data');
    
    setIsAuthenticated(false);
    setError(null);
    setRateLimitInfo(null);
    
    // Force page refresh to ensure UI updates (same as login)
    setTimeout(() => {
      window.location.reload();
    }, 100); // Shorter delay for logout since no database operations
  };

  return {
    isAuthenticated,
    isLoading,
    error,
    rateLimitInfo,
    authenticate,
    logout,
  };
};
